/**
 * ChatBell — admin topbar bell for incoming public / CRM chat messages.
 *
 * - Polls `/api/chat/notify-poll` every 8 s.
 * - Badge shows pending + unread-in-human conversations.
 * - Plays a soft bell sound whenever the total count *increases*.
 * - Mute toggle persists in localStorage (`rescigo.chat_bell.muted`).
 * - Click → navigates to `/crm/chats`.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellSlash, ChatCircleDots } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 8000;
const BELL_MP3 =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_rescigo-master-panel/artifacts/tdn0zi55_floraphonic-servant-bell-ring-2-211683.mp3";
const MUTE_KEY = "rescigo.chat_bell.muted";

function getToken() {
  return localStorage.getItem("rescigo_token") || "";
}

export default function ChatBell() {
  const [count, setCount] = useState(0);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const lastRef = useRef(0);
  const audioRef = useRef(null);

  useEffect(() => {
    // Pre-warm audio element (mobile browsers require user-gesture; the
    // page click that mounted the shell is usually enough for admins.)
    audioRef.current = new Audio(BELL_MP3);
    audioRef.current.preload = "auto";
    audioRef.current.volume = 0.6;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/chat/notify-poll`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const n = d.total_unread || 0;
        setCount(n);
        // Ring only if the count went UP since the previous tick.
        if (n > lastRef.current && lastRef.current !== 0 && !muted) {
          const p = audioRef.current?.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
        // Also ring on first-load when there's already an unread queue.
        if (lastRef.current === 0 && n > 0 && !muted) {
          const p = audioRef.current?.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
        lastRef.current = n;
      } catch { /* silent — the topbar keeps rendering */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [muted]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    localStorage.setItem(MUTE_KEY, next ? "1" : "0");
  };

  return (
    <div className="flex items-center gap-1" data-testid="topbar-chat-bell">
      <Link
        to="/crm/chats"
        data-testid="topbar-chat-bell-link"
        title={count > 0 ? `${count} unread chat message(s)` : "CRM Chats"}
        className="relative p-2 rounded-md text-[#5C5A55] hover:text-[#2D5A27] hover:bg-[#F0EFEA] transition-colors"
      >
        <ChatCircleDots size={20} weight={count > 0 ? "fill" : "regular"} />
        {count > 0 && (
          <span
            data-testid="topbar-chat-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C25D38] text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-white animate-pulse"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </Link>
      <button
        onClick={toggleMute}
        data-testid="topbar-chat-bell-mute"
        title={muted ? "Chat sound muted" : "Chat sound on"}
        className="p-2 rounded-md text-[#8A8780] hover:text-[#1A1A1A] hover:bg-[#F0EFEA] transition-colors"
      >
        {muted ? <BellSlash size={16} /> : <Bell size={16} />}
      </button>
    </div>
  );
}
